import React, { useState } from "react";
import {
  Code,
  Database,
  Webhook,
  Lock,
  ChevronDown,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const apiEndpoints = [
  {
    method: "GET",
    endpoint: "/api/v1/properties",
    description: "Retrieve property information and configuration",
    example: `{
  "property_id": "123",
  "name": "Grand Hotel",
  "rooms": [
    {
      "id": "101",
      "type": "deluxe",
      "capacity": 2
    }
  ]
}`,
  },
  {
    method: "POST",
    endpoint: "/api/v1/reservations",
    description: "Create a new reservation",
    example: `{
  "fileId": "GP1234",
  "yourRefId": "UA7hdf4",
  "guest": {
    "companyId": "489d0941-a99d-4d59-b655-b1b900906586",
    "contactId": "60aaafd2-b704-41e1-8879-b1b900906632"
  },
  "state": "OPTION",
  "marketing": {
    "source": "Corporate",
    "segment": "MICE",
    "channel": "Corporate"
  }
}`,
  },
  {
    method: "GET",
    endpoint: "/api/v1/availability",
    description: "Check room availability",
    example: `{
  "start_date": "2025-03-15",
  "end_date": "2025-03-20",
  "available_rooms": [
    {
      "room_id": "101",
      "type": "deluxe",
      "rate": 199.99
    }
  ]
}`,
  },
];

const apiFeatures = [
  {
    icon: Code,
    title: "RESTful API",
    description: "Modern REST API with JSON payloads and comprehensive documentation",
  },
  {
    icon: Database,
    title: "Real-time Sync",
    description: "Comprehensive and standardized data synchronization.",
  },
  {
    icon: Webhook,
    title: "Webhooks",
    description: "Event-driven architecture with customizable webhook notifications",
  },
  {
    icon: Lock,
    title: "Security",
    description: "OAuth 2.0 authentication and role-based access control",
  },
];

export function ApiFeatures() {
  const [expandedEndpoint, setExpandedEndpoint] = useState<number | null>(null);

  const toggleEndpoint = (index: number) => {
    setExpandedEndpoint(expandedEndpoint === index ? null : index);
  };

  return (
    <div className="bg-gray-900 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl text-white mb-4">
            Powerful API Documentation
          </h2>
          <p className="text-xl text-gray-300">
            Integrate with our comprehensive API suite designed for developers
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* API Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {apiFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-gray-800 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-[#4A8DE0] rounded-lg p-3">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="ml-4 text-lg text-white">{feature.title}</h3>
                  </div>
                  <p className="text-gray-400">{feature.description}</p>
                </div>
              );
            })}
          </div>

          {/* API Examples */}
          <div className="space-y-2">
            {apiEndpoints.map((endpoint, index) => (
              <div key={index} className="bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleEndpoint(index)}
                  className="w-full px-4 py-2 bg-gray-700 flex items-center justify-between hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <span
                      className={`px-2 py-1 rounded text-xs font-mono ${
                        endpoint.method === "GET"
                          ? "bg-green-900 text-green-300"
                          : "bg-blue-900 text-blue-300"
                      }`}
                    >
                      {endpoint.method}
                    </span>
                    <span className="text-gray-300 font-mono text-sm">
                      {endpoint.endpoint}
                    </span>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 transition-transform ${
                      expandedEndpoint === index ? "transform rotate-180" : ""
                    }`}
                  />
                </button>
                {expandedEndpoint === index && (
                  <div className="p-4">
                    <p className="text-gray-400 mb-2">{endpoint.description}</p>
                    <pre className="bg-gray-900 rounded p-4 overflow-x-auto">
                      <code className="text-gray-300 text-sm">
                        {endpoint.example}
                      </code>
                    </pre>
                  </div>
                )}
              </div>
            ))}
            <div className="text-center mt-8">
              <Link
                to="/docs"
                className="inline-flex items-center text-[#4A8DE0] hover:text-[#3C7CD6] font-medium"
              >
                Access the full documentation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}